import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { ImagePlus, X } from "lucide-react";
import { StatefulButton } from "./beui/StatefulButton";
import { Button } from "./beui/Button";

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export function WorkReportComposer({ text, images, disabled, pending, busy, saved, onText, onImages, onSubmit,
  label = "New report", fieldLabel = "Report", actionLabel = "Post report", retryLabel = "Retry report" }: {
  text: string; images: File[]; disabled: boolean; pending: boolean; busy: boolean; saved: boolean;
  onText: (text: string) => void; onImages: (images: File[]) => void; onSubmit: (event: FormEvent) => void;
  label?: string; fieldLabel?: string; actionLabel?: string; retryLabel?: string;
}) {
  const fieldId = useId();
  const picker = useRef<HTMLInputElement>(null);
  const [previews, setPreviews] = useState<string[]>([]), [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const urls = images.map(image => URL.createObjectURL(image)); setPreviews(urls);
    return () => urls.forEach(url => URL.revokeObjectURL(url));
  }, [images]);
  return <form onSubmit={onSubmit} className={`wu-composer ${fieldLabel === "Report" ? "" : "wu-comment-form"}`} aria-label={label}>
    <label htmlFor={fieldId}>{label}</label>
    <textarea id={fieldId} aria-label={fieldLabel} maxLength={2000} rows={3} className="field"
      placeholder={fieldLabel === "Report" ? "What changed? What’s next? Any blockers?" : `${fieldLabel} on this report…`} value={text} disabled={disabled || busy} readOnly={pending} onChange={event => onText(event.target.value)} />
    {images.length > 0 && <div className="wu-draft-images">{images.map((image, index) => <div key={`${index}:${image.name}`}>
      <img src={previews[index]} alt={`Selected image ${index + 1}: ${image.name}`} />
      <Button type="button" size="icon" aria-label={`Remove selected image ${index + 1}`} disabled={disabled || busy || pending} onClick={() => onImages(images.filter((_image, position) => position !== index))}><X className="h-4 w-4" /></Button>
    </div>)}</div>}
    {error && <p role="alert" className="mt-2 text-sm text-brand-red">{error}</p>}
    <div className="wu-composer-actions">
      <input ref={picker} type="file" multiple disabled={disabled || busy || pending} accept="image/jpeg,image/png,image/webp" aria-label={`Choose ${fieldLabel.toLowerCase()} images`} className="hidden" onChange={event => {
        if (disabled || busy || pending) { event.target.value = ""; return; }
        const selected = [...(event.target.files || [])]; event.target.value = "";
        if (selected.length + images.length > 5) { setError("Choose up to five images."); return; }
        if (selected.some(image => !["image/jpeg", "image/png", "image/webp"].includes(image.type) || !image.size || image.size > MAX_IMAGE_BYTES)) {
          setError("Choose JPG, PNG or WebP images up to 10 MiB each."); return;
        }
        setError(null); onImages([...images, ...selected]);
      }} />
      <Button type="button" variant="outline" size="sm" className="rounded-full" disabled={disabled || busy || pending || images.length >= 5} onClick={() => picker.current?.click()}><ImagePlus className="h-4 w-4" aria-hidden="true" />Images</Button>
      <span className="wu-character-count">{text.length}/2000</span>
      <StatefulButton type="submit" size="sm" state={busy ? "loading" : saved ? "success" : "idle"} loadingText="Saving…" successText="Saved" aria-label={pending ? retryLabel : actionLabel}
        disabled={disabled || !text.trim()} className="rounded-full" >{pending ? retryLabel : actionLabel}</StatefulButton>
    </div>
    <p className="wu-image-limit">JPG, PNG or WebP · 5 images max · 10 MiB each</p>
  </form>;
}
